/**
 * Middleware-authored values mirrored into the provider portal.
 *
 * Every value in this file reflects a definition owned by `@behuman/middleware`.
 * Do NOT edit these in isolation — they must simply reflect what middleware
 * defines; when a middleware value changes, update its mirror here (and in
 * patient-web's copy of this file) in the same change.
 *
 * It has been decided not to reference the middleware definitions directly, to
 * avoid creating a dependency link from the frontends to middleware. This can
 * be revisited in the future — e.g. these values could move to a separate
 * 'contracts' package shared by both sides.
 */

// Defined in packages/middleware/src/model/clinical-summary/overview/types.ts
// (OVERVIEW_DOC_TYPE_SYSTEM / OVERVIEW_DOC_TYPE_CODE): the DocumentReference.type
// coding that identifies a generated Clinical Summary Overview document.
export const OVERVIEW_DOC_TYPE_SYSTEM = 'https://behuman.health/document-type'
export const OVERVIEW_DOC_TYPE_CODE = 'ClinicalSummaryOverview'

// The code create-order.handler.ts stamps when the patient picks the draw site
// (`patientChoosesLocation`): the requisition is carried by the patient rather than
// faxed, so it is filed under a Documents-visible type instead of 'Order', which the
// patient portal hides.
export const LAB_REQUISITION_DOC_TYPE_CODE = 'LabRequisition'

// Defined in packages/middleware/src/super/utils/project-configure.utils.ts
// (labAccountIdentifiers): the placeholder lab account numbers `POST /super/project-configure`
// stamps on every provider Organization that has none, so a freshly configured project has
// labs to order from. No lab issued them — an account number can only come from the lab — so
// they are treated as "not set" wherever a real one would be used, and the requisition prints
// no `Account #:` line until a super admin enters the real value.
export const SEEDED_LAB_ACCOUNT_NUMBERS: readonly string[] = [
  'INV-00001',
  'LC-00001',
  'AR-00001',
  'ES-00001',
  'QD-00001',
]

// Defined in packages/middleware/src/order/handlers/create-order.handler.ts
// (docTypeCode, same document-type system as above): the DocumentReference.type
// codes stamped on the requisition PDF an order flow generates. They identify
// the requisition itself, as opposed to any other document filed against the
// same order.
export const ORDER_REQUISITION_DOC_TYPE_CODES = [
  'Order',
  LAB_REQUISITION_DOC_TYPE_CODE,
  'Referral',
] as const

// Defined in packages/middleware/src/model/risks-gaps/types.ts
// (RISKS_GAPS_DOC_TYPE_CODE; same document-type system as the overview): the
// DocumentReference.type coding that identifies a generated Risks & Gaps report.
export const RISKS_GAPS_DOC_TYPE_CODE = 'RisksGapsReport'

// Defined in packages/middleware/src/visit/visit.service.ts (BEHUMAN_DOCUMENT_TYPE_SYSTEM):
// the local document-type system for notes we author. Progress notes carry
// code 'ProgressNote' under this system; the Health Gorilla-specific coding is
// applied only at export time by the send-resources-to-hg bot.
export const BEHUMAN_DOCUMENT_TYPE_SYSTEM = 'https://behuman.health/document-type'

// Defined in packages/middleware/src/scribe/handlers/scribe-artifact.ts
// (SCRIBE_TRANSCRIPT_CODE / SCRIBE_DRAFT_CODE): the two internal scribe
// artifacts, both under BEHUMAN_DOCUMENT_TYPE_SYSTEM. Neither is a clinical
// document — keep both out of chart document lists.
export const SCRIBE_TRANSCRIPT_DOC_TYPE_CODE = 'ScribeTranscript'
export const KATE_TRANSCRIPT_DOC_TYPE_CODE = 'KatePreVisitTranscript'
export const SCRIBE_DRAFT_DOC_TYPE_CODE = 'ScribeDraft'
// Defined in packages/middleware/src/visit/handlers/progress-note-addendum.contract.ts
// (ADDENDUM_DOC_TYPE_CODE): a signed amendment appended to a locked Progress
// Note. Its own type code, not 'ProgressNote' — that is what keeps addenda out
// of the Health Gorilla subscription and scopes them in the AccessPolicy.
export const PROGRESS_NOTE_ADDENDUM_DOC_TYPE_CODE = 'ProgressNoteAddendum'
// Defined alongside it (ADDENDUM_REASON_EXTENSION_URL): why the amendment was
// written. `valueCode` is one of correction | clarification |
// additional-information | other.
export const ADDENDUM_REASON_EXTENSION_URL = 'https://behuman.health/addendum-reason'

export const PROGRESS_NOTE_DOC_TYPE_CODE = 'ProgressNote'

// Defined in packages/middleware/src/followup/handlers/followup-document.utils.ts
// (VISIT_SUMMARY_DOC_TYPE_CODING): the patient visit summary, the AI-written,
// provider-reviewed document saved and sent from a signed progress note. Written
// under BEHUMAN_DOCUMENT_TYPE_SYSTEM; rows from before PD-1741 used the www.
// system, so search it by code alone.
export const VISIT_SUMMARY_DOC_TYPE_CODE = 'PatientInstructions'

// Billing V2 ledger vocabulary. Source of truth:
// packages/middleware/src/billing-v2/billing-v2-shared.ts (frontends do not
// import middleware — keep these in sync with that file). Claim statuses and
// billing entities are NOT mirrored here: their web mirror already lives in
// pages/Billing/utils.ts (1:1 with the encounter-billing-status tag codes).

// (BILLING_CASE_EPOCH): start of the Billing V2 era on the DOS axis
// (Encounter.period.start). Encounters with DOS >= epoch are captured by the
// sweep; pre-epoch encounters only via an explicit billing tag.
export const BILLING_CASE_EPOCH = '2026-06-01'

// (CASE_PAYMENT_STATUS_VALUES): case-level payment rollup, derived from the
// payment rows — the `paymentStatus` string on the case DTO.
export const CASE_PAYMENT_STATUS_VALUES = [
  'no_payment',
  'awaiting_payer',
  'ready_to_process',
  'partially_paid',
  'paid',
  'failed',
] as const
export type CasePaymentStatus = (typeof CASE_PAYMENT_STATUS_VALUES)[number]

// (SUBMISSION_ROUTE_VALUES): how the claim leaves (or will leave) the building —
// the `submissionRoute` string on the case DTO.
export const SUBMISSION_ROUTE_VALUES = ['none', 'manual', 'bridge', 'stedi'] as const
export type SubmissionRoute = (typeof SUBMISSION_ROUTE_VALUES)[number]

// Defined in packages/middleware/src/super/handlers/fhir-systems.ts (NPI_SYSTEM) and
// packages/middleware/src/plugins/stedi/billing-provider.ts (EIN_SYSTEM): the
// Organization.identifier systems the claim billing identity lives under — written by
// the super-admin Update Organization form, read by the 837 pre-flight. Values are stored
// as bare digits (NPI 10, EIN 9).
export const NPI_IDENTIFIER_SYSTEM = 'http://hl7.org/fhir/sid/us-npi'
export const EIN_IDENTIFIER_SYSTEM = 'urn:oid:2.16.840.1.113883.4.4'

// Defined in packages/middleware/src/super/handlers/upsert-terminologies.handler.ts
// (ICD10CM_URL): the ICD-10-CM CodeSystem the terminology upload loads, the system of
// every diagnosis coding the billing pickers expand and write.
export const ICD10CM_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm'

// Defined in packages/middleware/src/encounter-billing/credential-guard.ts
// (RENDERING_PROVIDER_MISSING_CODE, PD-2017): `data.code` of the charge quote's refusal
// for a deleted or unfound rendering provider — the visit cannot be billed until reassigned.
export const RENDERING_PROVIDER_MISSING_CODE = 'RENDERING_PROVIDER_MISSING'

// Defined in packages/middleware/src/billing-v2/utils/managed-case.ts
// (managedCaseRefusal): the PD-1739 externally-managed lock message. Once a case
// has `claimSubmittedAt` set the clearinghouse owns the claim — the backend 400s
// manual money/status actions with this exact text, and the UI shows the same
// text instead of offering the action.
const MANAGED_ROUTE_DISPLAY: Record<string, string> = { stedi: 'Stedi', bridge: 'Bridge' }

export function managedCaseRefusal(submissionRoute: string): string {
  const provider = MANAGED_ROUTE_DISPLAY[submissionRoute] ?? submissionRoute
  return `Managed by ${provider}. Do not submit a separate claim through another billing system.`
}

// (PAYMENT_METHOD_VALUES): how a billing_payment row was (or will be) paid.
export const PAYMENT_METHOD_VALUES = ['card', 'vcc', 'ach_eft', 'check'] as const
export type PaymentMethod = (typeof PAYMENT_METHOD_VALUES)[number]

// (PAYMENT_ROW_STATUS_VALUES): lifecycle of a single billing_payment row —
// the `lastPaymentStatus` string on the case payments rollup.
export const PAYMENT_ROW_STATUS_VALUES = [
  'processing',
  'processed',
  'recorded',
  'failed',
  'refunded',
] as const
export type PaymentRowStatus = (typeof PAYMENT_ROW_STATUS_VALUES)[number]

// Defined in packages/middleware/src/copay/patient-invoice-kind.contract.ts
// (PATIENT_INVOICE_KINDS / EOB_RESPONSIBILITY_KINDS): the four kinds of patient money an
// encounter can carry — the `kind` parameter of the copay endpoints and of the case detail's
// patientInvoices — and the three the insurance EOB splits the patient responsibility into
// (Record payment's breakdown never includes self-pay).
export const PATIENT_INVOICE_KINDS = ['copay', 'deductible', 'coinsurance', 'self-pay'] as const
export type PatientInvoiceKind = (typeof PATIENT_INVOICE_KINDS)[number]
export const EOB_RESPONSIBILITY_KINDS = ['copay', 'deductible', 'coinsurance'] as const
export type EobResponsibilityKind = (typeof EOB_RESPONSIBILITY_KINDS)[number]

// Defined in packages/middleware/src/utils/self-pay.ts (SELF_PAY_COVERAGE_REFERENCE /
// SELF_PAY_STATUS_EXTENSION_URL / SELF_PAY_STATUS_VALUES): the self-pay visit marker booking
// writes into Appointment.supportingInformation, and the extension that says whether the
// fee was collected (the Unpaid badge reads it; it is never derived from the status).
export const SELF_PAY_COVERAGE_REFERENCE = 'Coverage/self-pay'
export const SELF_PAY_STATUS_EXTENSION_URL = 'https://behuman.health/self-pay-status'
export const SELF_PAY_STATUS_VALUES = ['unpaid', 'paid', 'refunded'] as const
export type SelfPayStatus = (typeof SELF_PAY_STATUS_VALUES)[number]

// (CASE_EVENT_ACTION_VALUES): actions journaled in billing_case_event — the
// `action` string on the case detail's event history.
export const CASE_EVENT_ACTION_VALUES = [
  'case_created',
  'claim_status_changed',
  'entity_changed',
  'eob_entered',
  'payment_recorded',
  'payment_failed',
  'payment_refunded',
  'claim_refreshed',
  'case_voided',
  'route_changed',
  'claim_submitted',
  'claim_blocked',
  'claim_failed',
] as const
export type CaseEventAction = (typeof CASE_EVENT_ACTION_VALUES)[number]

// Defined in packages/middleware/src/billing-v2/handlers/billing-report.contract.ts
// (ACCOUNTING_REPORT_VALUES / ACCOUNTING_GROUP_BY_VALUES / ACCOUNTING_DETAILS_CAP): the
// Accounting reports tab's views, its group-by dimensions ('method' only with 'payments') and
// the on-screen details cap — the cards and the summary always cover the whole selection.
export const ACCOUNTING_REPORT_VALUES = ['revenue', 'payments', 'outstanding'] as const
export type AccountingReport = (typeof ACCOUNTING_REPORT_VALUES)[number]
export const ACCOUNTING_GROUP_BY_VALUES = [
  'entity',
  'state',
  'provider',
  'payer',
  'method',
] as const
export type AccountingGroupBy = (typeof ACCOUNTING_GROUP_BY_VALUES)[number]
export const ACCOUNTING_DETAILS_CAP = 5000

// Defined in packages/middleware/src/super/handlers/fhir-systems.ts
// (PRACTITIONER_QUALIFICATION_SYSTEM): the coding system for the degree on a
// `Practitioner.qualification` element — MD, DO, NP, RN, PA…
export const PRACTITIONER_QUALIFICATION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v2-0360'

// Defined in packages/middleware/src/practitioner/qualification.util.ts
// (QUALIFICATION_LICENSE_SPECIALTY_EXTENSION_URL): a license's specialty on a
// `Practitioner.qualification` element (`valueCode`). Optional — only NP/RN
// degrees carry one. Where present it is the more precise credential, which is
// why `primaryCredentialSuffix` in that file prefers it over the bare degree.
export const QUALIFICATION_LICENSE_SPECIALTY_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/qualification-license-specialty'

// Defined in packages/middleware/src/messages/constants.ts
// (THREAD_DELIVERY_STATE_*): the per-participant delivery pointer on a message
// thread's header Communication. `lastDeliveredAt` is the `sent` of the newest
// message that participant's client has fetched, and is what the sender's
// Delivered receipt is derived from. Written by
// POST /v2/threads/acknowledge-delivery.
export const THREAD_DELIVERY_STATE_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/thread-delivery-state'
export const THREAD_DELIVERY_STATE_PARTICIPANT_URL = 'participant'
export const THREAD_DELIVERY_STATE_LAST_DELIVERED_AT_URL = 'lastDeliveredAt'

// Defined in packages/middleware/src/messages/constants.ts
// (THREAD_MAX_RECIPIENTS): recipients allowed on one thread, excluding its
// creator. Enforced server-side by CreateThreadDto's @ArrayMaxSize; mirrored
// here so the thread-creation form can validate before submitting.
export const THREAD_MAX_RECIPIENTS = 5

// Defined in packages/middleware/src/model/fhir/source.ts (IMPORTED_TAG).
export const IMPORTED_TAG_SYSTEM = 'https://behuman.health/tags'
export const IMPORTED_TAG_CODE = 'imported'

// Defined in packages/middleware/src/patient-merge/patient-merge.constants.ts
// (MERGED_TAG_TOKEN, PD-1986): the `meta.tag` a merge puts on the retired Patient
// (Restore removes it). Patient pickers leave tagged records out.
export const MERGED_TAG_TOKEN = 'https://behuman.health/tags|merged'

// Defined in packages/middleware/src/visit/visit.cda.ts (AI_DISCLOSURE_HEADING /
// AI_DISCLOSURE_TEXT, PD-1613): the section that closes every AI-assisted note,
// after Follow-Up and before any addendum. The C-CDA carries the same words.
export const AI_DISCLOSURE_HEADING = 'AI Documentation Disclosure'
export const AI_DISCLOSURE_TEXT =
  'This clinical note was generated with the assistance of an AI documentation tool. Prior to the visit, the patient was informed that an AI scribing tool may be used to assist with documentation and consented to its use. The provider has reviewed this note in its entirety and assumes full responsibility for its content.'

// Defined in packages/middleware/src/visit/handlers/lock-progress-note.handler.ts
// (Provenance.activity, v3-DocumentCompletion `AU` = "authenticated"): the
// attestation Provenance the lock and the addendum signature write — the legal
// signature, carrying the signer and the signed instant.
export const ATTESTATION_ACTIVITY_CODE = 'AU'

// Defined in packages/middleware/src/model/fhir/encounter-class.ts
// (CARE_COORDINATOR_ENCOUNTER_CLASS): Encounter.class of a care-coordinator
// session; not a provider visit, so visit-history rules skip it.
export const CARE_COORDINATOR_ENCOUNTER_CLASS_SYSTEM =
  'https://behuman.health/fhir/CodeSystem/encounter-class'
export const CARE_COORDINATOR_ENCOUNTER_CLASS_CODE = 'care-coordinator'

// Defined in packages/middleware/src/visit/visit.service.ts (SCRIBE_TAG_SYSTEM,
// UNREVIEWED_AI_DRAFT_TAG_CODE, PD-1330): written into the CREATE of a note
// auto-created at generation completion; the first save after review clears it.
export const SCRIBE_TAG_SYSTEM = 'https://behuman.health/scribe'
export const UNREVIEWED_AI_DRAFT_TAG_CODE = 'unreviewed-ai-draft'

// Defined in packages/middleware/src/order/handlers/triage-fax-lab.handler.ts
// (FAX_DR_IDENTIFIER_SYSTEM).
export const FAX_DR_IDENTIFIER_SYSTEM = 'https://behuman.health/fax-diagnostic-report'

// Defined in packages/middleware/src/model/incoming-fax/internal/from-fhir.ts
// (FAX_TRIAGE_DOCUMENT_OUTPUT_TYPE): the fax-triage Task.output type of a fax
// triaged as a plain Document (PD-1869), pointing at the fax's DocumentReference.
export const FAX_TRIAGE_DOCUMENT_OUTPUT_TYPE = 'DocumentReference'

// Defined in packages/middleware/src/chart-records/handlers/chart-records.contract.ts
// (CHART_RECORD_RESOURCE_TYPES).
export const CHART_RECORD_RESOURCE_TYPES = [
  'DocumentReference',
  'DiagnosticReport',
  'RequestGroup',
] as const
export type ChartRecordResourceType = (typeof CHART_RECORD_RESOURCE_TYPES)[number]

// Defined in packages/middleware/src/chart-records/combined-pdf/merge-records-pdf.ts
// (COMBINABLE_CONTENT_TYPES): the file types a Labs/Imaging result or a Documents record can
// carry into the combined PDF (PD-1685). Decides which rows can be ticked.
export const COMBINABLE_CONTENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/tiff',
] as const

// Defined in packages/middleware/src/chart-records/combined-pdf/combined-pdf.contract.ts
// (COMBINED_PDF_MAX_RECORDS): the most records one combined PDF can hold.
export const COMBINED_PDF_MAX_RECORDS = 100

// Defined in packages/middleware/src/chart-records/chart-record-permissions.ts
// (SELF_MANAGED_DOCUMENT_CODES): insurance cards and identity documents have
// their own delete flows, which also detach the references Coverage/Patient
// hold, so they are not removable through the generic chart-record action.
export const SELF_MANAGED_DOCUMENT_CODES = [
  'InsuranceCardFront',
  'InsuranceCardBack',
  'PatientIdentityFront',
]

// Defined in packages/middleware/src/encounter-billing/claim.builder.ts
// (BILLING_CLAIM_ENCOUNTER_IDENTIFIER_SYSTEM): the Claim.identifier system that
// ties a Claim to its Encounter. Middleware uses it as the per-encounter
// idempotency key, so it is also the one reliable way to find THE Claim for an
// encounter — the frontend must resolve it the same way the server does, or the
// two disagree about which Claim a visit is billed under.
export const BILLING_CLAIM_ENCOUNTER_IDENTIFIER_SYSTEM =
  'https://behuman.health/fhir/billing-claim-encounter'

// Defined in packages/middleware/src/encounter-billing/diagnosis-pointer-guard.ts
// (MAX_DIAGNOSIS_POINTERS_PER_LINE): an 837P service line carries at most this many
// diagnosis pointers; the billing save, Submit and Retry refuse a line over it (PD-2013).
export const MAX_DIAGNOSIS_POINTERS_PER_LINE = 4

// Defined in packages/middleware/src/appointment/dto/visit-reason.constants.ts
// (VISIT_REASONS): the reason-for-visit catalog. POST /appointment/book rejects
// any id outside it, so this list must stay in step with middleware's.
// The catalog's 'im-not-sure' entry ("I'm not sure — help me choose") is
// deliberately omitted here: it is patient-voiced and only offered in the
// patient portal's booking flow.
// `group` mirrors middleware's section comments and drives the Select's optgroups.
//
// Defined in the same file (VISIT_REASON_SOURCES): who chose the reason, recorded
// on the reasonCode's visit-reason-source extension. Every booking made from this
// console is staff-chosen, so it always sends 'staff'; the patient portal omits
// the field and middleware falls back to 'patient'.
export const VISIT_REASON_SOURCE_STAFF = 'staff'

export const VISIT_REASONS: ReadonlyArray<{ id: string; label: string; group: string }> = [
  {
    id: 'preventive-care-longevity-visit',
    label: 'Preventive Care & Longevity Visit',
    group: 'General services',
  },
  { id: 'cancer-risk-assessment', label: 'Cancer Risk Assessment', group: 'General services' },
  {
    id: 'cancer-screening-consultation',
    label: 'Cancer Screening Consultation',
    group: 'General services',
  },
  {
    id: 'chronic-disease-management',
    label: 'Chronic Disease Management',
    group: 'General services',
  },
  { id: 'care-coordination', label: 'Care Coordination', group: 'General services' },
  {
    id: 'medication-review-optimization',
    label: 'Medication Review & Optimization',
    group: 'General services',
  },
  { id: 'breast-cancer-screening', label: 'Breast Cancer Screening', group: 'Cancer screening' },
  {
    id: 'cervical-cancer-screening',
    label: 'Cervical Cancer Screening',
    group: 'Cancer screening',
  },
  {
    id: 'colorectal-cancer-screening',
    label: 'Colorectal Cancer Screening',
    group: 'Cancer screening',
  },
  { id: 'lung-cancer-screening', label: 'Lung Cancer Screening', group: 'Cancer screening' },
  {
    id: 'prostate-cancer-screening',
    label: 'Prostate Cancer Screening',
    group: 'Cancer screening',
  },
  {
    id: 'hereditary-cancer-risk-genetic-testing',
    label: 'Hereditary Cancer Risk & Genetic Testing',
    group: 'Cancer screening',
  },
  { id: 'diabetes-management', label: 'Diabetes Management', group: 'Chronic conditions' },
  {
    id: 'obesity-weight-management',
    label: 'Obesity & Weight Management',
    group: 'Chronic conditions',
  },
  {
    id: 'high-blood-pressure-management',
    label: 'High Blood Pressure Management',
    group: 'Chronic conditions',
  },
  {
    id: 'heart-disease-management',
    label: 'Heart Disease Management',
    group: 'Chronic conditions',
  },
  {
    id: 'kidney-disease-management',
    label: 'Kidney Disease Management',
    group: 'Chronic conditions',
  },
  { id: 'medical-records-review', label: 'Medical Records Review', group: 'Care support' },
  {
    id: 'screening-test-results-review',
    label: 'Screening & Test Results Review',
    group: 'Care support',
  },
  {
    id: 'clinical-trial-enrollment-support',
    label: 'Clinical Trial Enrollment Support',
    group: 'Care support',
  },
]
// Defined in packages/middleware/src/dosespot/model/identifiers.ts
// (doseSpotPatientSystem): `Patient.identifier` carries the DoseSpot patient id
// under `https://dosespot.com/clinic/{clinicId}`. The system is CLINIC-SCOPED and
// the browser does not know the clinic id, so only the prefix is mirrored — it is
// enough to answer "has this patient ever been launched into DoseSpot?", which is
// what gates the chart-view write-back sync.
export const DOSESPOT_PATIENT_SYSTEM_PREFIX = 'https://dosespot.com/clinic/'

// Defined in packages/middleware/src/dosespot/model/identifiers.ts
// (DOSESPOT_STATUS_EXT): DoseSpot's TRANSMISSION axis (`eRxSent`, `Error`,
// `Printed`, …) kept verbatim on the synced `MedicationRequest`. Presence of this
// extension is also how the chart recognises a DoseSpot-originated row — the
// prescription identifier is clinic-scoped and the browser does not know the
// clinic id — and the write-back sync always writes it for exactly that reason.
export const DOSESPOT_STATUS_EXT = 'https://behuman.health/dosespot/status'

// Defined in packages/middleware/src/dosespot/model/identifiers.ts
// (DOSESPOT_MEDICATION_STATUS_EXT): DoseSpot's CLINICAL axis (`Active`,
// `Completed`, `Discontinued`, …), verbatim. Deliberately not defaulted by the
// sync, so its absence means DoseSpot said nothing rather than said `Unknown`.
export const DOSESPOT_MEDICATION_STATUS_EXT = 'https://behuman.health/dosespot/medication-status'

// Defined in packages/middleware/src/dosespot/model/identifiers.ts
// (DOSESPOT_PRESCRIBER_ID_EXT): the prescription's own raw `PrescriberId`, which
// is authoritative over the launcher. This extension present alongside an ABSENT
// `requester` is the unmapped-prescriber flag — there is no separate boolean.
export const DOSESPOT_PRESCRIBER_ID_EXT = 'https://behuman.health/dosespot/prescriber-id'

// Defined in packages/middleware/src/dosespot/model/identifiers.ts
// (DOSESPOT_DRUG_SYSTEM): the third coding on `medicationCodeableConcept`,
// carrying DoseSpot's proprietary `DispensableDrugId`. It is the only drug code
// guaranteed present — a prescription carries no RxNorm and no NDC of its own.
export const DOSESPOT_DRUG_SYSTEM = 'https://dosespot.com/dispensable-drug'

// Defined in packages/middleware/src/dosespot/model/identifiers.ts
// (DOSESPOT_DEFAULT_PHARMACY_EXT): the patient's default pharmacy (PD-1386) — one
// complex extension on `Patient` whose simple-named `valueString` sub-extensions
// (pharmacyId, ncpdpId, storeName, address1, address2, city, state, zip, phone,
// fax, specialties) are the add-time snapshot the Demographics card renders from.
// The card matches on extension PRESENCE, per the DoseSpot mirror convention.
export const DOSESPOT_DEFAULT_PHARMACY_EXT = 'https://behuman.health/dosespot/default-pharmacy'

// Defined in packages/middleware/src/scribe/handlers/record-duration.handler.ts
// (RECORD_DURATION_ENCOUNTER_MISSING): the marker in the 404 that
// `POST /v2/scribe/sessions/{encounterId}/duration` returns when the Encounter
// does not exist. The call leave edge silently skips exactly this 404 (the
// chart-side flow passes a patient id as the encounter id) and logs any other.
export const RECORD_DURATION_ENCOUNTER_MISSING = 'no Encounter to record a duration on'

// Defined in packages/middleware/src/scribe/handlers/record-duration.contract.ts
// (RECORD_DURATION_SEGMENT_PREFIX): the endpoint rejects a segment id without
// it, which keeps the Zoom caller's ids apart from the scribe session ids.
export const RECORD_DURATION_SEGMENT_PREFIX = 'zoom:'

// Mirrors FAX_TASK_ACTIONS in
// packages/middleware/src/care-tasks/fax-task-actions.schema.ts (PD-1513).
// This file is a mirror: never edit it in isolation — a middleware-side change
// must update it in the same change.
//
// DRIFT RISK, recorded deliberately: the createFaxTask contract validates
// against the middleware list, while the Actions picker renders from this copy.
// If the two fall out of step the server accepts a code the UI never offers (or
// the UI offers one the server rejects), with nothing to catch it. Kept as a
// mirror because AGENTS.md forbids the frontends referencing middleware
// definitions directly; that convention explicitly anticipates being revisited
// by moving such values into a contracts package. Whoever revisits it: the
// middleware list already lives in a *.schema.ts precisely so it can cross that
// boundary, so this is a small, self-contained candidate to convert first.
export const FAX_TASK_ACTIONS = [
  { code: 'complete-form', label: 'Complete form' },
  { code: 'provider-review', label: 'Provider review' },
  { code: 'provider-signature', label: 'Provider signature' },
  { code: 'fax-document-back', label: 'Fax document back' },
  { code: 'contact-patient', label: 'Contact patient' },
  { code: 'contact-requesting-office', label: 'Contact requesting office' },
  { code: 'schedule-appointment', label: 'Schedule appointment' },
  { code: 'upload-document', label: 'Upload or attach document' },
  { code: 'review-results', label: 'Review results' },
  { code: 'request-information', label: 'Request additional information' },
  { code: 'other', label: 'Other' },
] as const
// Defined in packages/middleware/src/zocdoc/model/identifiers.ts
// (ZOCDOC_PATIENT_SYSTEM): `Patient.identifier` → Zocdoc's own `patient_id`.
// An identifier rather than an extension because it is step 1 of the patient
// match and must be searchable (`Patient?identifier=<system>|<id>`). Global, not
// practice-scoped — Zocdoc's patient_id is account-wide.
export const ZOCDOC_PATIENT_SYSTEM = 'https://behuman.health/zocdoc-patient'

// Defined in packages/middleware/src/zocdoc/model/identifiers.ts
// (ZOCDOC_PROVIDER_SYSTEM): `Practitioner.identifier` → Zocdoc's own
// `provider_id` (`pr_…`), the provider half of a `pr_…|lo_…` entity. The person
// is the Practitioner; the per-location half is Zocdoc's own and is read from
// Zocdoc when needed, not mirrored. Searchable both ways, which is how a Zocdoc
// event naming a provider resolves its practitioner — this identifier is now the
// entire provider mapping.
export const ZOCDOC_PROVIDER_SYSTEM = 'https://behuman.health/zocdoc-provider'

// Defined in packages/middleware/src/zocdoc/model/identifiers.ts
// (ZOCDOC_APPOINTMENT_SYSTEM): `Appointment.identifier` → Zocdoc's own
// `appointment_id`. Binds the visit itself, so a change arriving from Zocdoc
// with only an appointment id can find the beHuman appointment and act on it.
// On the Appointment rather than the Encounter: the Encounter points at the
// Appointment, so one identifier reaches both.
export const ZOCDOC_APPOINTMENT_SYSTEM = 'https://behuman.health/zocdoc-appointment'

// Defined in packages/middleware/src/insurance/insurance.constants.ts
// (ZOCDOC_INSURANCE_REQUEST_SOURCE): `meta.source` of an insurance request filed
// from a Zocdoc booking's insurance rather than typed by the patient. Same
// questionnaire and review; the source only changes what the console calls it.
export const ZOCDOC_INSURANCE_REQUEST_SOURCE = 'zocdoc'

// Defined in packages/middleware/src/auth-v2/auth-v2-shared.ts
// (AUTH_AUDIT_EVENT_TYPE_VALUES): the structured auth_audit_event types the Auth console
// filters on.
export const AUTH_AUDIT_EVENT_TYPE_VALUES = [
  'login_success',
  'login_failed',
  'lockout_triggered',
  'mfa_sent',
  'mfa_verified',
  'mfa_failed',
  'refresh_reuse_revoked',
  'refresh_channel_mismatch',
  'logout',
  'session_expired_absolute',
  'session_expired_idle',
  'password_reset_requested',
  'password_reset_completed',
  'admin_revoked',
  'admin_lockout_cleared',
  'admin_trust_revoked',
  'admin_otp_override_set',
  'admin_otp_override_removed',
] as const

// Defined in packages/middleware/src/auth-v2/otp-override.service.ts
// (OTP_OVERRIDE_MAX_DAYS): the furthest a fixed-2FA-code override may expire.
export const OTP_OVERRIDE_MAX_DAYS = 365
// Defined in packages/middleware/src/boards/boards-resource.ts (TODO_GROUP.label):
// the label of the fixed To-Do group every Care Queue board carries. Group ids
// are random UUIDs, so every server-side task creator resolves this group by its
// label, and so must anything on this side that files a task.
export const TODO_GROUP_LABEL = 'To-Do'
// Defined in packages/middleware/src/facility-discovery/facility-discovery-shared.schema.ts.
// Modalities the discovery search accepts (keys of RADIOLOGY_COMPENDIUM that have a
// Places query phrase). Must stay equal to RADIOLOGY_DISCOVERY_MODALITIES in middleware.
export const RADIOLOGY_DISCOVERY_MODALITIES = [
  'mri',
  'ct_scan',
  'xray',
  'ultrasound',
  'womens_health',
  'pet_scan',
  'nuclear_medicine',
  'osteoporosis_detection',
] as const

// Prefix minted for a legacy/seeded Organization's placeId when Places is
// unavailable — see LEGACY_PLACE_ID_PREFIX in that same middleware file.
export const LEGACY_PLACE_ID_PREFIX = 'org:'

// Defined in the same middleware file (DISCOVERY_KINDS): what the discovery
// search is looking for. `modality` only means something for radiology, and a
// lab search additionally requires a vendor.
export const DISCOVERY_KINDS = ['radiology', 'lab'] as const

// Defined in the same middleware file (LAB_VENDORS): the lab chains discovery
// can search. Must stay equal to LAB_VENDORS in middleware.
export const LAB_VENDORS = ['labcorp', 'quest'] as const

// Defined in the same middleware file (LAB_VENDOR_PARENT_NAME): the parent lab
// Organization's exact name. A lab location is tied to its vendor only through
// `partOf`, never a code, so the name is what both sides key on.
export const LAB_VENDOR_PARENT_NAME = {
  labcorp: 'Labcorp',
  quest: 'Quest Diagnostics',
} as const satisfies Record<(typeof LAB_VENDORS)[number], string>

// Defined in packages/middleware/src/briefing/handlers/briefing-day.contract.ts: the daily
// briefing's item groups (in display order), item kinds and dismiss reasons (PD-1936).
export const BRIEFING_GROUPS = ['before-visits', 'needs-decision', 'labs-docs'] as const
export const BRIEFING_ITEM_KINDS = [
  'kate-question',
  'kate-callback',
  'message',
  'task',
  'result',
  'guidance',
  'note',
] as const
export const BRIEFING_DISMISS_REASONS = ['addressed', 'not-relevant', 'duplicate'] as const
// Defined in packages/middleware/src/model/insurance-plan.schema.ts
// (INSURANCE_PLAN_TYPE_SYSTEM): the `meta.tag` system on a CoverageEligibilityResponse
// carrying the payer-verified plan type (the clearinghouse's X12 271 EB04 signal mapped
// to our vocabulary); the codes are `InsurancePlanType` from @behuman/core-contract.
export const INSURANCE_PLAN_TYPE_SYSTEM = 'https://behuman.health/fhir/insurance-plan-type'

// Defined in packages/middleware/src/insurance/handlers/insurance-verdicts.contract.ts
// (INSURANCE_VERDICTS_MAX_SUBJECTS): the most subjects one `insuranceVerdicts` call accepts;
// a larger batch is split into calls of this size.
export const INSURANCE_VERDICTS_MAX_SUBJECTS = 500
